import React, { Component } from 'react'
import PropTypes from 'prop-types'
import uuid from 'uuid'
import { get, isEmpty } from 'lodash'
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import { withRouter, Link } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import queryString from 'query-string'

import * as authActions from 'redux/modules/auth'
import { notification, modal, setPageCode } from 'redux/modules/app'
import { CircleLoader } from 'components/loaders'
import { Login as LoginForm } from 'containers/forms/auth'
import { Button, ButtonGroup, Module } from 'components/misc'
import { Title, HeaderPlain } from 'components/layout'
import {
  getFullName,
  getToken,
	getActiveRole,
	getActiveOrganization
  // multipleOrganizations
} from 'utils/token'
import { USER_TYPES } from 'constants/enums'
import appConfig from 'config'

@withRouter
@connect(
  state => ({
    user: state.auth.user,
		activeOrganization: state.auth.organization,
		autoLogging: state.auth.autoLogging
  }),
  { ...authActions, notification, push, modal }
)
class Login extends Component {
  static propTypes = {
    user: PropTypes.object,
		activeOrganization: PropTypes.object,
		autoLogging: PropTypes.bool,
    location: PropTypes.object,
    saveUser: PropTypes.func,
    login: PropTypes.func,
    logout: PropTypes.func,
    removeOrganization: PropTypes.func,
    setActiveOrganization: PropTypes.func,
		userAutologin: PropTypes.func,
    setPageCode: PropTypes.func,
		setAutoLoggingFlag: PropTypes.func,
    push: PropTypes.func,
    modal: PropTypes.func.isRequired,
    notification: PropTypes.func
  }

  state = {
    organizations: []
  }

  componentDidMount() {
    const { autoLogging, user } = this.props
    const query = queryString.parse(get(this.props, 'location.search'))

		if (get(query, 'id')) {
		    // if there are no user data stored
			if (!getToken(user)) {
				// execute autologin flow directly
				this.handlePerformAutoLogin(get(query, 'id'))
			// if there are user data stored, but autologin flow is not in progress
			} else if (!autoLogging) {
				// execute autologin flow (with logout existing user first)
				this.handleAutoLogin(get(query, 'id'))
			}
		}
  }

  setSentryUser = (body, role) => {
    const personId = get(body, 'personId')

    Sentry.setUser({
      personId,
      role,
      locale: get(body, 'personLocale')
    })
  }

  handleSubmit = fields => {
    this.props.removeOrganization()
    return this.props
      .login(fields)
      .then(({ body }) => this.handleSaveUser(body))
      .catch(({ body }) => {
        this.props.notification(body && body.userMessage, 'error')
      })
  }

  handleAutoLogin = id => {
    const { user } = this.props

    if (getToken(user)) {
      return this.props
        .logout()
        .then(() => {
          this.handlePerformAutoLogin(id)
        })
        .catch(() => console.log('logout failed'))
    }
    this.handlePerformAutoLogin(id)
  }

  handlePerformAutoLogin = id => {
		this.props.removeOrganization()
		this.props.setAutoLoggingFlag(true)

    this.props
      .userAutologin({ id })
      .then(({ body }) => this.handleSaveUser(body.userInfo))
      .catch(({ body }) => {
        this.props.notification(body && body.userMessage, 'error')
        if (appConfig.foUrl) {
          window.location.replace(`${appConfig.foUrl}?isAwardStaff=true`)
        } else if (get(location, 'state.inactivity')) {
          this.props.push({
            pathname: `${appConfig.publicPath}/login`
          })
        }
      })
  }

  handleSaveUser = userData => {
    this.props
      .saveUser(userData)
      .then(() => {
        this.props.notification('Logged', {
          status: 'success',
          intlKey: 'notifications.you_have_been_logged_in'
        })

        const query = queryString.parse(get(this.props, 'location.search'))

        const pageCode = get(query, "pageCode")

        this.props.setPageCode(pageCode)

        const prefferedOrganisation = get(userData, 'prefferedOrganisation', {})

				// if preffered organization is present, set it as active org and redirect using this org
        if (prefferedOrganisation.id) {
					this.props.setActiveOrganization(prefferedOrganisation)
					return this.handleRedirectBasedOnRole(prefferedOrganisation)
        }

				const { activeOrganization } = this.props
				// in some specific cases, active organization is not present (is empty) in props, so take it directly from userData
				const actOrg = isEmpty(activeOrganization) ? getActiveOrganization(userData) : activeOrganization

        this.setSentryUser(userData, get(userData, 'userTypes'))

        // if no preffered org is present, redirect based on active org
				return this.handleRedirectBasedOnRole(actOrg)
      })
      // .then(() => {
      //   if (multipleOrganizations(userData)) {
      //     // Solve this without need for unreliable setTimeout
      //     setTimeout(
      //       () =>
      //         this.props.modal('modal-organization-switcher', null, {
      //           isFromLogin: true
      //         }),
      //       700
      //     )
      //   }
      // })
      .catch(() => {
        this.props.notification('Something went wrong', {
          status: 'error',
          intlKey: 'notifications.something_went_wrong'
        })
      })
  }

  handleRedirectBasedOnRole = org => {
    sessionStorage.setItem('boUserLoginId', uuid.v1())
		this.props.setAutoLoggingFlag(false)
    const path = get(USER_TYPES[getActiveRole(org)], 'defaultRedirect', `${appConfig.publicPath}/participants`)
    this.props.push(path)
  }

  render() {
    const { user, logout } = this.props
    const query = queryString.parse(get(this.props, 'location.search'))

    return (
      <div>
        <HeaderPlain />
        <div className="container">
          {get(query, 'id') &&
            <div className="base-margin">
              <CircleLoader size={60} />
              <Title
                h1
                className="beta text--center"
                intlKey="moduleTitle.logging_in"
              >
                Logging in
              </Title>
            </div>}
          {!get(query, 'id') &&
            <div>
              <Module
                style={{
                  width: 460,
                  margin: '20px auto'
                }}
                primary
              >
                {!get(location, 'state.notAuthorized') &&
                  <Title
                    h1
                    className="beta reset--margin-top"
                    intlKey="moduleTitle.login"
                  >
                    Login
                  </Title>}

                {!getToken(user) &&
                  <div>
                    <LoginForm onSubmit={this.handleSubmit} {...this.props} />
                  </div>}

                {getToken(user) &&
                  <div>
                    {!get(location, 'state.notAuthorized') &&
                      !get(query, 'id') &&
                      <div>
                        <p>
                          <FormattedMessage
                            id="label.you_are_already_logged_as"
                            defaultMessage="You are already logged as"
                          />
                          {' '}
                          {getFullName(user)}
                        </p>
                        <ButtonGroup>
                          <Button onClick={logout} intlKey="button.logout">
                            Logout
                          </Button>
                        </ButtonGroup>
                      </div>}

                    {get(location, 'state.notAuthorized') &&
                      <div className="text--center">
                        <p>
                          <FormattedHTMLMessage
                            id="label.user_does_not_have_permissions"
                            defaultMessage="User <strong>{user}</strong> doesn't have permissions to see this content."
                            values={{
                              user: getFullName(user)
                            }}
                          />
                        </p>
                        <ButtonGroup center>
                          <Link className="btn secondary" to={`${appConfig.publicPath}/`}>
                            <FormattedMessage
                              id="button.go_to_homepage"
                              defaultMessage="Go to Homepage"
                            />
                          </Link>
                          <Button
                            onClick={logout}
                            intlKey="button.login_with_another_account"
                          >
                            Login with another account
                          </Button>
                        </ButtonGroup>
                      </div>}
                  </div>}
              </Module>
            </div>}
        </div>
      </div>
    )
  }
}

export default Login
